import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';

import DefaultLink from 'components/common/DefaultLink';
import * as Routes from 'helpers/routes';
import AnchoSweetSteakChipotleCrema from 'images/familyHome/ancho-sweet-steak-chipotle-crema.jpg';
import ChickenScampiButter from 'images/familyHome/chicken-scampi-butter.jpg';
import CreoleStyleSalmon from 'images/familyHome/creole-style-salmon.jpg';
import FarroSpicyArrabbiata from 'images/familyHome/farro-spicy-arrabbiata-sauce.jpg';
import JumboTurkeyMeatballVeggieBake from 'images/familyHome/jumbo-turkey-meatball-veggie-bake.jpg';
import PauseIcon from 'images/familyHome/pause.svg';
import PretzelCrustedChicken from 'images/familyHome/pretzel-crusted-chicken.jpg';
import SaucyGarlicPorkMedallions from 'images/familyHome/saucy-garlic-pork-medallions.jpg';
import ScallopsParmesanGarlicRisotto from 'images/familyHome/scallops-parmesan-garlic-risotto.jpg';
import ShrimpWhiteCheddarRisotto from 'images/familyHome/shrimp-white-cheddar-risotto.jpg';
import SirloinSteakMaitreDHotelButter from 'images/familyHome/sirloin-steak-maitre-dhotel-butter.jpg';
import { STANDARD } from 'lib/constants';
import { logEvent, events } from 'services/analytics';

const TRANSITION = { duration: 0.4, type: 'spring' };
const TOTAL_VISIBLE = 5;

const FamilyHomeMealCard = ({ imageSrc, mealTitle, mealDescription, index }) => {
  const middleIndex = Math.floor(TOTAL_VISIBLE / 2);
  const distanceFromMiddle = Math.abs(index - middleIndex);

  let scale;
  let zIndex;
  if (distanceFromMiddle === 0) {
    scale = 1;
    zIndex = 20;
  } else if (distanceFromMiddle === 1) {
    scale = 0.9;
    zIndex = 10;
  } else {
    scale = 0.8;
    zIndex = 0;
  }

  return (
    <motion.li
      layout
      animate={{ scale, opacity: 1 }}
      initial={{ opacity: 0.2, scale: 0.2 }}
      className={`z-${zIndex} -mx-6 flex h-72 w-52 shrink-0 flex-col rounded-2xl bg-white-100 p-2 shadow-xl bp2:h-80 bp2:w-64 bp2:p-4`}
      transition={TRANSITION}
    >
      <img src={imageSrc} alt="" className="mb-2 !h-40 w-full rounded-xl object-cover" />
      <div className="mx-auto flex flex-col items-center bp2:px-2">
        <h2 className="h4 pb-2 text-center text-[18px] text-green-140 bp2:text-[20px]">
          {mealTitle}
        </h2>
        <p className="textXs bp2:textSm pb-2 text-center font-medium text-green-140">
          {mealDescription}
        </p>
      </div>
    </motion.li>
  );
};

FamilyHomeMealCard.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  mealTitle: PropTypes.string.isRequired,
  mealDescription: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
};

const IMAGE_MAP = {
  shrimp_white_cheddar_risotto: ShrimpWhiteCheddarRisotto,
  saucy_garlic_pork_medallions: SaucyGarlicPorkMedallions,
  pretzel_crusted_chicken: PretzelCrustedChicken,
  scallops_parmesan_garlic_risotto: ScallopsParmesanGarlicRisotto,
  farro_spicy_arrabbiata_sauce: FarroSpicyArrabbiata,
  creole_style_salmon: CreoleStyleSalmon,
  ancho_seared_steak_chipotle_crema: AnchoSweetSteakChipotleCrema,
  chicken_scampi_butter: ChickenScampiButter,
  jumbo_turkey_meatball_veggie_bake: JumboTurkeyMeatballVeggieBake,
  sirloin_steak_maitre_dhotel_butter: SirloinSteakMaitreDHotelButter,
};

const MealCarousel = ({ data: { title, subtitle, ctaButton, meals: mealsData } }) => {
  const initialOrder = useMemo(() => {
    if (!mealsData) return [];
    return mealsData.map(meal => ({
      ...meal,
      imageSrc: IMAGE_MAP[meal.key],
    }));
  }, [mealsData]);

  const [order, setOrder] = useState(initialOrder);
  const [isPaused, setIsPaused] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion || isPaused) return;

    const interval = setInterval(() => {
      setOrder(prevOrder => [...prevOrder.slice(1), prevOrder[0]]);
    }, 1500);
    return () => clearInterval(interval);
  }, [isPaused, prefersReducedMotion]);

  return (
    <section className="-mt-px justify-center bg-family-beige-100 py-8 bp2:py-20">
      <motion.h2
        className="h1 mb-4 text-center text-green-140 bp1:mb-6"
        initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, type: 'spring' }}
        viewport={{ once: true, amount: 0.5 }}
      >
        {title}
      </motion.h2>
      <motion.p
        className="textMd bp1:h5 mb-8 px-8 text-center text-green-140 bp1:mb-12"
        initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, type: 'spring' }}
        viewport={{ once: true, amount: 0.5 }}
      >
        {subtitle}
      </motion.p>
      <motion.div
        className="relative w-full overflow-x-clip"
        initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, type: 'spring' }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="absolute left-0 top-0 z-50 h-full w-16 bg-gradient-to-r from-family-beige-100/80 to-transparent" />
        <div className="absolute right-0 top-0 z-50 h-full w-16 bg-gradient-to-l from-family-beige-100/80 to-transparent" />
        <motion.ul className="mx-auto mb-4 flex max-w-7xl flex-row justify-center">
          {order.slice(0, TOTAL_VISIBLE).map((meal, index) => (
            <FamilyHomeMealCard key={meal.key} {...meal} index={index} />
          ))}
        </motion.ul>
        <div className="relative z-[60] mx-auto mb-10 flex max-w-4xl justify-end px-4 bp2:mb-16">
          {!prefersReducedMotion && (
            <button
              type="button"
              aria-pressed={isPaused}
              aria-label={isPaused ? 'Play Animation' : 'Pause Animation'}
              onClick={() => setIsPaused(paused => !paused)}
            >
              <img src={PauseIcon} alt="" />
            </button>
          )}
        </div>
        <DefaultLink
          to={`${Routes.getBrandRoute({ brand: STANDARD, isFamily: true }).intro}?funnel=hide_plans`}
          isExternal={true}
          variation="Primary"
          className="btn btnLg mx-auto mb-6 flex w-fit border-none !bg-green-140 !text-white-110 hover:brightness-90"
          onClick={() =>
            logEvent(events.familyHome.submitted, {
              urlPath: Routes.getBrandRoute({ brand: STANDARD, isFamily: true }).intro,
            })
          }
        >
          {ctaButton}
        </DefaultLink>
      </motion.div>
    </section>
  );
};

MealCarousel.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    ctaButton: PropTypes.string.isRequired,
    meals: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        mealTitle: PropTypes.string.isRequired,
        mealDescription: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
};

export default MealCarousel;
